import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { apiErrorMessage } from '../../../utils/errors';
import {
  useListPalletSizes,
  useCreatePalletSize,
  useUpdatePalletSize,
  useArchivePalletSize,
  useUnarchivePalletSize,
} from '../../../features/loads/api';
import SearchInput from '../../../components/SearchInput/SearchInput';

const EMPTY = {
  name: '',
  type: '',
  lengthIn: '',
  widthIn: '',
  heightIn: '',
  weightTons: '',
};

const asNum = (v: unknown) => {
  const s = (v ?? '').toString().trim();
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

export default function PalletSizesTab() {
  const { data: rows = [], isLoading } = useListPalletSizes();
  const createItem = useCreatePalletSize();
  const updateItem = useUpdatePalletSize();
  const archiveItem = useArchivePalletSize();
  const unarchiveItem = useUnarchivePalletSize();

  const [q, setQ] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const [draft, setDraft] = useState(EMPTY);
  const [creating, setCreating] = useState(false);

  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [archivingId, setArchivingId] = useState(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();

    const base = rows.filter((r: any) => (showArchived ? true : !r.deletedAt));

    if (!s) return base;

    return base.filter((r: any) =>
      [
        r.name,
        r.type,
        r.lengthIn?.toString(),
        r.widthIn?.toString(),
        r.heightIn?.toString(),
        r.weightTons?.toString(),
      ].some((v) => (v ?? '').toLowerCase().includes(s))
    );
  }, [rows, q, showArchived]);

  const onCreate = () => {
    const payload = {
      name: draft.name.trim(),
      type: draft.type.trim(),
      lengthIn: asNum(draft.lengthIn),
      widthIn: asNum(draft.widthIn),
      heightIn: asNum(draft.heightIn),
      weightTons: asNum(draft.weightTons),
    };

    if (
      !payload.name ||
      payload.lengthIn == null ||
      payload.widthIn == null ||
      payload.heightIn == null
    ) {
      return toast.warn('Name, Length, Width and Height are required.');
    }

    setCreating(true);
    createItem.mutate(payload, {
      onSuccess: () => {
        toast.success('Pallet size added.');
        setDraft(EMPTY);
      },
      onError: (e) => toast.error(apiErrorMessage(e, 'Create failed.')),
      onSettled: () => setCreating(false),
    });
  };

  const onSave = () => {
    const payload = {
      name: editForm.name.trim(),
      type: editForm.type.trim(),
      lengthIn: asNum(editForm.lengthIn),
      widthIn: asNum(editForm.widthIn),
      heightIn: asNum(editForm.heightIn),
      weightTons: asNum(editForm.weightTons),
    };

    if (
      !payload.name ||
      payload.lengthIn == null ||
      payload.widthIn == null ||
      payload.heightIn == null
    ) {
      return toast.warn('Name, Length, Width and Height are required.');
    }

    setSaving(true);
    updateItem.mutate(
      { id: editId as string, data: payload },
      {
        onSuccess: () => {
          toast.success('Pallet size updated.');
          setEditId(null);
        },
        onError: (e) => toast.error(apiErrorMessage(e, 'Update failed.')),
        onSettled: () => setSaving(false),
      }
    );
  };

  const onArchive = (row: any) => {
    if (!window.confirm(`Archive "${row.name}"?`)) return;
    setArchivingId(row.id);

    archiveItem.mutate(row.id, {
      onSuccess: () => {
        toast.success('Template archived.');
        if (editId === row.id) setEditId(null);
      },
      onError: (e) => toast.error(apiErrorMessage(e, 'Archive failed.')),
      onSettled: () => setArchivingId(null),
    });
  };

  const onUnarchive = (row: any) => {
    setArchivingId(row.id);

    unarchiveItem.mutate(row.id, {
      onSuccess: () => toast.success('Template restored.'),
      onError: (e) => toast.error(apiErrorMessage(e, 'Restore failed.')),
      onSettled: () => setArchivingId(null),
    });
  };

  const StatusChip = ({ archived }: { archived: boolean }) => {
    const style = {
      padding: '2px 8px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      border: '1px solid #e5e7eb',
      background: archived ? '#f3f4f6' : '#dcfce7',
      color: archived ? '#374151' : '#166534',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
    };
    return <span style={style}>{archived ? 'Archived' : 'Active'}</span>;
  };

  return (
    <div className="card card--bleed-top">
      {/* Create row */}
      <div className="grid grid--5">
        <div className="form-control">
          <label>Name *</label>
          <input
            value={draft.name}
            onChange={(e) => setDraft((f) => ({ ...f, name: e.target.value }))}
            placeholder="Standard 48x40"
          />
        </div>
        <div className="form-control">
          <label>Type</label>
          <input
            value={draft.type}
            onChange={(e) => setDraft((f) => ({ ...f, type: e.target.value }))}
            placeholder="Super Sack"
          />
        </div>
        <div className="form-control">
          <label>Length (in) *</label>
          <input
            value={draft.lengthIn}
            onChange={(e) => setDraft((f) => ({ ...f, lengthIn: e.target.value }))}
          />
        </div>
        <div className="form-control">
          <label>Width (in) *</label>
          <input
            value={draft.widthIn}
            onChange={(e) => setDraft((f) => ({ ...f, widthIn: e.target.value }))}
          />
        </div>
        <div className="form-control">
          <label>Height (in) *</label>
          <input
            value={draft.heightIn}
            onChange={(e) => setDraft((f) => ({ ...f, heightIn: e.target.value }))}
          />
        </div>
        <div className="form-control">
          <label>Weight (tons)</label>
          <input
            value={draft.weightTons}
            onChange={(e) => setDraft((f) => ({ ...f, weightTons: e.target.value }))}
            placeholder="optional"
          />
        </div>
      </div>

      <div className="actions" style={{ justifyContent: 'space-between', marginTop: 10 }}>
        <button className="btn btn--primary" onClick={onCreate} disabled={creating}>
          {creating ? 'Adding...' : 'Add Pallet Size'}
        </button>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          <span className="muted">Show archived</span>
        </label>
      </div>

      <div className="search-row">
        <SearchInput
          className="search-input"
          wrapperClassName="search-input-clearable--narrow"
          placeholder="Search pallet sizes…"
          value={q}
          onValueChange={setQ}
        />
      </div>

      <div className="card card--bleed-top">
        {isLoading ? (
          <div className="skeleton h-40 w-full" />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Length (in)</th>
                <th>Width (in)</th>
                <th>Height (in)</th>
                <th>Weight (tons)</th>
                <th>Status</th>
                <th className="col-actions ta-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row: any) => {
                const editing = editId === row.id;
                const archived = !!row.deletedAt;

                return (
                  <tr key={row.id} style={archived ? { opacity: 0.65 } : undefined}>
                    <td>
                      {editing ? (
                        <input
                          value={editForm.name}
                          onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                        />
                      ) : (
                        row.name
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          value={editForm.type}
                          onChange={(e) => setEditForm((f) => ({ ...f, type: e.target.value }))}
                        />
                      ) : (
                        (row.type ?? <span className="muted">—</span>)
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          value={editForm.lengthIn}
                          onChange={(e) => setEditForm((f) => ({ ...f, lengthIn: e.target.value }))}
                        />
                      ) : (
                        row.lengthIn
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          value={editForm.widthIn}
                          onChange={(e) => setEditForm((f) => ({ ...f, widthIn: e.target.value }))}
                        />
                      ) : (
                        row.widthIn
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          value={editForm.heightIn}
                          onChange={(e) => setEditForm((f) => ({ ...f, heightIn: e.target.value }))}
                        />
                      ) : (
                        row.heightIn
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          value={editForm.weightTons}
                          onChange={(e) =>
                            setEditForm((f) => ({ ...f, weightTons: e.target.value }))
                          }
                          placeholder="optional"
                        />
                      ) : (
                        (row.weightTons ?? <span className="muted">—</span>)
                      )}
                    </td>

                    <td>
                      <StatusChip archived={archived} />
                    </td>

                    <td className="col-actions actions-cell ta-right">
                      {editing ? (
                        <>
                          <button className="btn btn--primary" onClick={onSave} disabled={saving}>
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button className="btn btn--ghost" onClick={() => setEditId(null)}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn btn--ghost"
                            disabled={archived}
                            title={archived ? 'Restore to edit' : ''}
                            onClick={() => {
                              setEditId(row.id);
                              setEditForm({
                                name: row.name ?? '',
                                type: row.type ?? '',
                                lengthIn: row.lengthIn ?? '',
                                widthIn: row.widthIn ?? '',
                                heightIn: row.heightIn ?? '',
                                weightTons: row.weightTons ?? '',
                              });
                            }}
                          >
                            Edit
                          </button>

                          {!archived ? (
                            <button
                              className="btn btn--ghost"
                              onClick={() => onArchive(row)}
                              disabled={archivingId === row.id}
                            >
                              {archivingId === row.id ? 'Archiving...' : 'Archive'}
                            </button>
                          ) : (
                            <button
                              className="btn btn--ghost"
                              onClick={() => onUnarchive(row)}
                              disabled={archivingId === row.id}
                            >
                              {archivingId === row.id ? 'Restoring...' : 'Restore'}
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}

              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted ta-center">
                    No pallet sizes found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
